import { Alert } from '@components/ui';
import type { CertificateExpiryStatus } from '../types/certificate.types';
import { formatExpiryRelative } from '../utils/certificateExpiry';

/**
 * URL de la mesa de ayuda -- mismo patron/variable que CertificateExpiryModal.tsx,
 * ver ese archivo para el porque (00-loop-prompt.md punto 4d).
 */
const SUPPORT_URL = import.meta.env.VITE_SUPPORT_URL || 'https://support.hypernovalabs.com/';

type ActionableExpiryStatus = Extract<CertificateExpiryStatus, 'yellow' | 'red'>;

interface CertificateExpiryBannerProps {
  status: ActionableExpiryStatus;
  nearestExpiresAt: Date | null;
}

const TITLE: Record<ActionableExpiryStatus, string> = {
  yellow: 'Certificado próximo a expirar',
  red: 'Certificado de firma expirado',
};

/**
 * Banner persistente en el layout de la app (no solo en Configuracion >
 * Informacion Fiscal y Certificados) -- a diferencia del pop-up, este NO
 * tiene boton de cierre: si el usuario ignora el pop-up, el banner sigue
 * visible en cada pantalla mientras el certificado siga vencido o por
 * vencer. Solo deja de renderizarse cuando useCertificateExpiryStatus()
 * vuelve a reportar 'green' (certificado renovado). Feedback 2026-08-12:
 * un pop-up cerrable por si solo no garantiza que el cliente actue.
 */
export function CertificateExpiryBanner({ status, nearestExpiresAt }: CertificateExpiryBannerProps) {
  const relative = formatExpiryRelative(status, nearestExpiresAt);

  return (
    <Alert
      variant={status === 'red' ? 'danger' : 'warning'}
      title={TITLE[status]}
      data-testid="certificate-expiry-banner"
      className="rounded-none border-x-0 border-t-0"
    >
      {relative && <span className="font-semibold">{relative}. </span>}
      Por favor contacte a nuestra mesa de ayuda (
      <a
        href={SUPPORT_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium underline underline-offset-2 hover:opacity-80"
        data-testid="certificate-expiry-banner-support-link"
      >
        {SUPPORT_URL}
      </a>
      ) para gestionar su renovación.
    </Alert>
  );
}
